import {
  formatDate,
  parseDate,
  toPrimitiveDateInput,
} from '@superdispatch/dates';
import { DateTime } from 'luxon';
import {
  getBookingTimeRange,
  PostingStepDTO,
} from '../../data/dto/PostingLoadDTO';

export type StepName = 'Pickup' | 'Delivery';
type DateType = NonNullable<PostingStepDTO['date_type']>;

const DATE_TYPE_LABELS: Record<DateType, string> = {
  estimated: 'estimated for',
  exact: 'exactly on',
  not_earlier_than: 'no earlier than',
  not_later_than: 'no later than',
};

export function getStepLabel(name: StepName, step: PostingStepDTO) {
  return step.date_type ? `${name} ${DATE_TYPE_LABELS[step.date_type]}` : name;
}

export function getStepRuleText(name: StepName, step: PostingStepDTO) {
  const date = formatStepDate(step);
  return date ? `${getStepLabel(name, step)} ${date}` : null;
}

export function toDate(value: string | null | undefined) {
  const date = parseDate(toPrimitiveDateInput(value), { format: 'JodaISO' });
  return date.isValid ? date : null;
}

function getRangeEnd({ date_type, scheduled_ends_at }: PostingStepDTO) {
  return date_type === 'estimated' ? toDate(scheduled_ends_at) : null;
}

// "No later than" uses its own date: its booking range starts today, so it
// would end today once the date has passed.
export function isPickupOpen(
  pickup: PostingStepDTO,
  today: DateTime = DateTime.local().startOf('day'),
) {
  const lastDay =
    pickup.date_type === 'not_later_than'
      ? toDate(pickup.scheduled_at)
      : getBookingTimeRange(pickup)[1];
  return lastDay != null && lastDay >= today;
}

export function formatStepDate(step: PostingStepDTO) {
  const start = toDate(step.scheduled_at);
  if (!start) return null;

  const end = getRangeEnd(step);
  return end ? formatDateRange(start, end) : formatFullDate(start);
}

function formatDateRange(start: DateTime, end: DateTime) {
  if (start.hasSame(end, 'day')) return formatFullDate(start);

  if (start.hasSame(end, 'month')) {
    return `${formatMonthDay(start)}–${end.day}, ${end.year}`;
  }

  if (start.hasSame(end, 'year')) {
    return `${formatMonthDay(start)} – ${formatFullDate(end)}`;
  }

  return `${formatFullDate(start)} – ${formatFullDate(end)}`;
}

export function formatMonthDay(date: DateTime) {
  return formatDate(date, { variant: 'ShortDate' });
}

function formatFullDate(date: DateTime) {
  return formatDate(date, { variant: 'Date' });
}
